import { useMemo } from 'react';
import { useTranslate } from 'nucleus-text';
// Need to import from timezoneSelector otherwise it creates a dependency cycle
import usePreferredTimezone from '@hooks/usePreferredTimezone';

export const DATE_TIME_FORMAT = Object.freeze({
  short: 'short',
  numeric: 'numeric'
});

const replaceTimezone = (parts: Intl.DateTimeFormatPart[], label: string): string =>
  parts
    .map(part => (part.type === 'timeZoneName' ? label : part.value))
    .join('')
    .replaceAll(/\s/g, ' '); // replace all whitespace characters with space for consistency

/**
 * Hook to get function that formats date and replaces timezone with phrase timezone when event timezone is preferred
 *
 */
const useTimezone = () => {
  const { appliedTimezone, preferredTimezone, eventTimezone, eventTimezoneLabels } = usePreferredTimezone();
  const { translate, date, locale } = useTranslate();

  return useMemo(
    () => ({
      date: (dateTime, options, ...rest) => {
        try {
          if (appliedTimezone && appliedTimezone !== preferredTimezone) {
            return new Intl.DateTimeFormat(locale, { timeZone: appliedTimezone, ...options }).format(dateTime);
          }

          // This renders a datetime without Phrase timezone strings
          if (preferredTimezone && eventTimezone !== preferredTimezone) {
            return new Intl.DateTimeFormat(locale, { timeZone: preferredTimezone, ...options }).format(dateTime);
          }

          const formatter = new Intl.DateTimeFormat(locale, {
            timeZone: preferredTimezone,
            ...options
          });

          return options?.timeZoneName === 'long'
            ? replaceTimezone(formatter.formatToParts(dateTime), translate(eventTimezoneLabels.long))
            : replaceTimezone(formatter.formatToParts(dateTime), translate(eventTimezoneLabels.short));
        } catch (error) {
          // noop - fall through to use default date formatter
        }
        return date(dateTime, options, ...rest);
      }
    }),
    [preferredTimezone, appliedTimezone, eventTimezone, eventTimezoneLabels, translate, date, locale]
  );
};

export default useTimezone;
